"use client";

import { getOrder } from "@/actions/getOrder";
import { formatIsoDate } from "@/utils/formatDate";
import { formatPrice } from "@/utils/priceUtils";
import Link from "@app/_components/Link";
import { Button, Heading, Input, Paragraph } from "@app/_components/ui";
import { useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import { useForm } from "@formspree/react";
import Image from "next/image";
import { useActionState, useRef } from "react";

export default function OrderManagementRoute() {
  const t = useTranslate();
  const { market } = useMarket().state;
  const [state, handleSubmit] = useForm("mgobqwjp");
  const [order, action, pending] = useActionState(getOrder, undefined);
  const formRef = useRef<HTMLFormElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  function getItemCheckboxes() {
    return formRef.current?.querySelectorAll<HTMLInputElement>(
      'input[data-item-checkbox][type="checkbox"]',
    );
  }

  function syncSelectAllCheckbox() {
    const checkboxes = getItemCheckboxes();
    const selectAll = selectAllRef.current;
    if (!checkboxes || !selectAll) return;

    const checkedCount = [...checkboxes].filter((cb) => cb.checked).length;
    selectAll.checked = checkedCount === checkboxes.length;
    selectAll.indeterminate =
      checkedCount > 0 && checkedCount < checkboxes.length;
  }

  function onSelectAllChange(event: React.ChangeEvent<HTMLInputElement>) {
    const checked = event.target.checked;
    getItemCheckboxes()?.forEach((checkbox) => {
      checkbox.checked = checked;
    });
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = false;
    }
  }

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const rawItems = formData.getAll("items");
    const items = rawItems.map((rawItem) => {
      const item = JSON.parse(String(rawItem));

      return {
        ...item,
        placedPrice: formatPrice(item.placedPrice, market.locale),
        extendedPrice: formatPrice(item.extendedPrice, market.locale),
      };
    });

    // FormData only stores strings, so JSON.stringify(items) is opaque to Mustache.
    // Submit a JSON body instead — Formspree exposes nested arrays to templates.
    void handleSubmit({
      orderDate: new Date(String(formData.get("orderDate"))).toLocaleDateString(
        market.locale,
      ),
      orderNumber: String(formData.get("orderNumber")),
      email: String(formData.get("email")),
      items,
    } as unknown as Parameters<typeof handleSubmit>[0]);
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col px-4 py-8 max-md:px-3 lg:col-span-9 lg:py-16 xl:col-span-10">
      {state.succeeded ? (
        <div className="flex flex-col items-center gap-y-4">
          <Heading as="h3" size={3}>
            {t("orderCancelSuccess")}
          </Heading>
          <Paragraph className="text-center">
            {t("orderCancelSuccessDescription")}
          </Paragraph>
          <Button variant="primary" size="lg" asChild>
            <Link locale={market.locale}>{t("continueShopping")}</Link>
          </Button>
        </div>
      ) : (
        <section>
          {order && !(order instanceof Error) ? (
            <form
              ref={formRef}
              onSubmit={onSubmit}
              className="flex flex-col gap-y-4"
            >
              <input
                type="hidden"
                name="orderNumber"
                value={order.orderNumber}
              />
              <input type="hidden" name="orderDate" value={order.created} />
              <input type="hidden" name="email" value={order.email} />
              <Heading as="h3" size={3}>
                {t("orderDetails")}
              </Heading>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                <span className="all-small-caps">{t("orderNumber")}:</span>
                <span>{order.orderNumber}</span>
                <span className="all-small-caps">{t("created")}:</span>
                <span>{formatIsoDate(order.created, market.locale)}</span>
                <span className="all-small-caps">{t("status")}:</span>
                <span>{order.status}</span>
                <span className="all-small-caps">{t("currency")}:</span>
                <span>{order.currency}</span>
                <span className="all-small-caps">{t("total")}:</span>
                <span>{formatPrice(order.total, market.locale)}</span>
                <span className="all-small-caps">{t("paymentMethod")}:</span>
                <span>{order.paymentMethod}</span>
              </div>
              <Heading as="h3" size={3}>
                {t("products")}
              </Heading>
              <div className="grid grid-cols-[20px_48px_1fr_auto_auto_auto] gap-x-4 gap-y-2">
                <div className="col-span-full grid grid-cols-subgrid border-b py-2">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    aria-label="Select all items"
                    onChange={onSelectAllChange}
                    className="place-self-center"
                  />
                  <span />
                  <Paragraph className="all-small-caps font-normal">
                    {t("item")}
                  </Paragraph>
                  <Paragraph className="all-small-caps justify-self-end font-normal">
                    {t("quantity")}
                  </Paragraph>
                  <Paragraph className="all-small-caps justify-self-end font-normal">
                    {t("price")}
                  </Paragraph>
                  <Paragraph className="all-small-caps justify-self-end font-normal">
                    {t("total")}
                  </Paragraph>
                </div>
                <ul className="col-span-full grid grid-cols-subgrid gap-y-2">
                  {order.lineItems.map((lineItem) => (
                    <li
                      key={lineItem.lineItemId}
                      className="col-span-full grid grid-cols-subgrid items-center"
                    >
                      <input
                        name="items"
                        value={JSON.stringify(lineItem)}
                        id={lineItem.code}
                        type="checkbox"
                        data-item-checkbox
                        onChange={syncSelectAllCheckbox}
                        className="place-self-center"
                      />
                      <figure className="relative size-12 self-start bg-primary-100">
                        <Image
                          fill
                          className="h-full w-full overflow-hidden object-contain mix-blend-multiply"
                          src={`${lineItem.imageUrl}?w=200&h=200&mode=max`}
                          alt={lineItem.displayName}
                        />
                      </figure>
                      <Paragraph className="all-small-caps font-normal">
                        {lineItem.displayName}
                      </Paragraph>
                      <Paragraph className="all-small-caps justify-self-end font-normal">
                        {lineItem.quantity}
                      </Paragraph>
                      <Paragraph className="all-small-caps justify-self-end font-normal">
                        {formatPrice(lineItem.placedPrice, market.locale)}
                      </Paragraph>
                      <Paragraph className="all-small-caps justify-self-end font-normal">
                        {formatPrice(lineItem.extendedPrice, market.locale)}
                      </Paragraph>
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                variant="primary"
                type="submit"
                disabled={state.submitting}
                isLoading={state.submitting}
                size="lg"
              >
                {t("cancelOrder")}
              </Button>
            </form>
          ) : (
            <form action={action} className="flex flex-col gap-y-4">
              <Heading as="h1" size={2}>
                {t("cancelOrder")}
              </Heading>
              <Paragraph>{t("cancelOrderDescription")}</Paragraph>
              <Heading as="h3" size={3}>
                {t("findOrder")}
              </Heading>
              <Input
                id="orderNumber"
                name="orderNumber"
                type="text"
                required
                label={t("orderNumber")}
              />
              <Input
                id="email"
                name="email"
                type="email"
                required
                label={t("email")}
                autoComplete="email"
              />
              {order && order instanceof Error ? (
                <p className="text-red-600">{order.message}</p>
              ) : null}
              <Button
                variant="primary"
                type="submit"
                disabled={pending}
                isLoading={pending}
                size="lg"
              >
                {t("findOrder")}
              </Button>
            </form>
          )}
        </section>
      )}
    </div>
  );
}
